import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'

const MAX_RETRY_ATTEMPTS = 10
const RETRY_INTERVAL_MS = 1000

export default function OrganizationInvitationCallback() {
  const navigate = useNavigate()

  const {
    loading,
    isAuthEnabled,
    organizations,
    isOrgLoading,
    switchOrganization,
  } = useAuth()

  const { isLoaded: clerkLoaded, userId } = useClerkAuth()

  const [retryCount, setRetryCount] = useState(0)
  const [status, setStatus] = useState<'loading' | 'waiting' | 'error'>('loading')
  const [statusMessage, setStatusMessage] = useState('Completing your invitation...')
  const hasRedirected = useRef(false)

  useEffect(() => {
    if (!isAuthEnabled) {
      navigate('/app', { replace: true })
    }
  }, [isAuthEnabled, navigate])

  useEffect(() => {
    if (!clerkLoaded || loading) {
      setStatus('loading')
      setStatusMessage('Signing you in...')
      return
    }

    if (!userId) {
      setStatus('loading')
      setStatusMessage('Completing sign-in...')
      return
    }

    if (isOrgLoading) {
      setStatus('loading')
      setStatusMessage('Loading your organization...')
      return
    }

    if (organizations.length > 0 && !hasRedirected.current) {
      hasRedirected.current = true
      setStatusMessage('Welcome! Redirecting to dashboard...')

      if (organizations.length === 1) {
        switchOrganization(organizations[0].id).then(() => {
          navigate('/app', { replace: true })
        })
      } else {
        navigate('/app', { replace: true })
      }
      return
    }

    setStatus('waiting')
    setStatusMessage('Setting up your account...')
  }, [
    clerkLoaded,
    userId,
    loading,
    isOrgLoading,
    organizations,
    retryCount,
    navigate,
    switchOrganization,
  ])

  useEffect(() => {
    if (status !== 'waiting') return

    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      setStatus('error')
      return
    }

    const timer = setTimeout(() => {
      setRetryCount(prev => prev + 1)
    }, RETRY_INTERVAL_MS)

    return () => clearTimeout(timer)
  }, [status, retryCount])

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full"
        >
          <div className="text-center mb-6">
            <img
              src="/axone_logo.png"
              alt="Axone Logo"
              className="h-16 w-auto mx-auto mb-6"
            />
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">
              Setup Taking Longer Than Expected
            </h1>
            <p className="text-slate-600 text-sm">
              Your account is being set up. Please wait a moment and try again,
              or contact your organization administrator if this persists.
            </p>
          </div>

          <button
            onClick={() => {
              setRetryCount(0)
              setStatus('waiting')
            }}
            className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>

          <p className="text-xs text-slate-500 text-center mt-6">
            Need help? Contact support at support@axoneai.app
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center"
      >
        <img
          src="/axone_logo.png"
          alt="Axone Logo"
          className="h-20 w-auto mx-auto mb-8"
        />
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-6" />
        <p className="text-lg font-medium text-slate-900 mb-2">
          {statusMessage}
        </p>
        <p className="text-sm text-slate-500">
          This will only take a moment...
        </p>
      </motion.div>
    </div>
  )
}
